/* eslint-disable */

const code =  `
// -----------------------------------------------------------------------------
//
// GLSL blending modes adapted from https://github.com/jamieowen/glsl-blend.
//
// -----------------------------------------------------------------------------
//
// The MIT License (MIT) Copyright (c) 2015 Jamie Owen
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
//
// -----------------------------------------------------------------------------

float blendAdd(float base, float blend) {
    return min(base + blend, 1.0);
}

vec3 blendAdd(vec3 base, vec3 blend) {
    return min(base + blend, vec3(1.0));
}

float blendAverage(float base, float blend) {
    return (base + blend) / 2.0;
}

vec3 blendAverage(vec3 base, vec3 blend) {
    return (base + blend) / 2.0;
}

float blendColorBurn(float base, float blend) {
    return (blend == 0.0) ? blend : max((1.0 - ((1.0 - base) / blend)), 0.0);
}

vec3 blendColorBurn(vec3 base, vec3 blend) {
    return vec3(
        blendColorBurn(base.r, blend.r),
        blendColorBurn(base.g, blend.g),
        blendColorBurn(base.b, blend.b)
    );
}

float blendColorDodge(float base, float blend) {
    return (blend == 1.0) ? blend : min(base / (1.0 - blend), 1.0);
}

vec3 blendColorDodge(vec3 base, vec3 blend) {
    return vec3(
        blendColorDodge(base.r, blend.r),
        blendColorDodge(base.g, blend.g),
        blendColorDodge(base.b, blend.b)
    );
}

float blendDarken(float base, float blend) {
    return min(blend, base);
}

vec3 blendDarken(vec3 base, vec3 blend) {
    return vec3(
        blendDarken(base.r, blend.r),
        blendDarken(base.g, blend.g),
        blendDarken(base.b, blend.b)
    );
}

float blendDifference(float base, float blend) {
    return abs(base - blend);
}

vec3 blendDifference(vec3 base, vec3 blend) {
    return abs(base - blend);
}

float blendExclusion(float base, float blend) {
    return base + blend - 2.0 * base * blend;
}

vec3 blendExclusion(vec3 base, vec3 blend) {
    return base + blend - 2.0 * base * blend;
}

float blendLighten(float base, float blend) {
    return max(blend, base);
}

vec3 blendLighten(vec3 base, vec3 blend) {
    return vec3(
        blendLighten(base.r, blend.r),
        blendLighten(base.g, blend.g),
        blendLighten(base.b, blend.b)
    );
}

float blendLinearBurn(float base, float blend) {
    // Note : Same implementation as BlendSubtractf
    return max(base + blend - 1.0, 0.0);
}

vec3 blendLinearBurn(vec3 base, vec3 blend) {
    // Note : Same implementation as BlendSubtract
    return max(base + blend - vec3(1.0), vec3(0.0));
}

float blendLinearDodge(float base, float blend) {
    // Note : Same implementation as BlendAdd
    return min(base + blend, 1.0);
}

vec3 blendLinearDodge(vec3 base, vec3 blend) {
    // Note : Same implementation as BlendAdd
    return min(base + blend, vec3(1.0));
}

float blendLinearLight(float base, float blend) {
    return blend < 0.5 ? blendLinearBurn(base, (2.0 * blend)) : blendLinearDodge(base, (2.0 * (blend - 0.5)));
}

vec3 blendLinearLight(vec3 base, vec3 blend) {
    return vec3(
        blendLinearLight(base.r, blend.r),
        blendLinearLight(base.g, blend.g),
        blendLinearLight(base.b, blend.b)
    );
}

float blendMultiply(float base, float blend) {
    return base * blend;
}

vec3 blendMultiply(vec3 base, vec3 blend) {
    return base * blend;
}

float blendNegation(float base, float blend) {
    return 1.0 - abs(1.0 - base - blend);
}

vec3 blendNegation(vec3 base, vec3 blend) {
    return vec3(1.0) - abs(vec3(1.0) - base - blend);
}

float blendOverlay(float base, float blend) {
    return base < 0.5 ? (2.0 * base * blend) : (1.0 - 2.0 * (1.0 - base) * (1.0 - blend));
}

vec3 blendOverlay(vec3 base, vec3 blend) {
    return vec3(
        blendOverlay(base.r, blend.r),
        blendOverlay(base.g, blend.g),
        blendOverlay(base.b, blend.b)
    );
}

float blendHardLight(float base, float blend) {
    return blendOverlay(blend, base);
}

vec3 blendHardLight(vec3 base, vec3 blend) {
    return blendOverlay(blend, base);
}

float blendPhoenix(float base, float blend) {
    return min(base, blend) - max(base, blend) + 1.0;
}

vec3 blendPhoenix(vec3 base, vec3 blend) {
    return min(base, blend) - max(base, blend) + vec3(1.0);
}

float blendPinLight(float base, float blend) {
    return (blend < 0.5) ? blendDarken(base, (2.0 * blend)) : blendLighten(base, (2.0 * (blend - 0.5)));
}

vec3 blendPinLight(vec3 base, vec3 blend) {
    return vec3(
        blendPinLight(base.r, blend.r),
        blendPinLight(base.g, blend.g),
        blendPinLight(base.b, blend.b)
    );
}

float blendReflect(float base, float blend) {
    return (blend == 1.0) ? blend : min(base * base / (1.0 - blend), 1.0);
}

vec3 blendReflect(vec3 base, vec3 blend) {
    return vec3(
        blendReflect(base.r, blend.r),
        blendReflect(base.g, blend.g),
        blendReflect(base.b, blend.b)
    );
}

float blendGlow(float base, float blend) {
    return blendReflect(blend, base);
}

vec3 blendGlow(vec3 base, vec3 blend) {
    return blendReflect(blend, base);
}

float blendScreen(float base, float blend) {
    return 1.0 - ((1.0 - base) * (1.0 - blend));
}

vec3 blendScreen(vec3 base, vec3 blend) {
    return vec3(
        blendScreen(base.r, blend.r),
        blendScreen(base.g, blend.g),
        blendScreen(base.b, blend.b)
    );
}

float blendSoftLight(float base, float blend) {
    return (blend < 0.5) ? (2.0 * base * blend + base * base * (1.0 - 2.0 * blend)) : (sqrt(base) * (2.0 * blend - 1.0) + 2.0 * base * (1.0 - blend));
}

vec3 blendSoftLight(vec3 base, vec3 blend) {
    return vec3(
        blendSoftLight(base.r, blend.r),
        blendSoftLight(base.g, blend.g),
        blendSoftLight(base.b, blend.b)
    );
}

float blendSubtract(float base, float blend) {
    return max(base + blend - 1.0, 0.0);
}

vec3 blendSubtract(vec3 base, vec3 blend) {
    return max(base + blend - vec3(1.0), vec3(0.0));
}

float blendVividLight(float base, float blend) {
    return (blend < 0.5) ? blendColorBurn(base, (2.0 * blend)) : blendColorDodge(base, (2.0 * (blend - 0.5)));
}

vec3 blendVividLight(vec3 base, vec3 blend) {
    return vec3(
        blendVividLight(base.r, blend.r),
        blendVividLight(base.g, blend.g),
        blendVividLight(base.b, blend.b)
    );
}

float blendHardMix(float base, float blend) {
    return (blendVividLight(base, blend) < 0.5) ? 0.0 : 1.0;
}

vec3 blendHardMix(vec3 base, vec3 blend) {
    return vec3(
        blendHardMix(base.r, blend.r),
        blendHardMix(base.g, blend.g),
        blendHardMix(base.b, blend.b)
    );
}
`

const regex = /float blend(\w+)\(/g
const modes = Array.from(code.matchAll(regex))
    .map(match => match[1])

export default code
export { modes }
